import type { ReactNode } from 'react';
import { api } from './api';

/**
 * ACRÓNIMOS DE LA OFICINA — el lado que PINTA.
 *
 * Pedido del dueño (2026-09-28): "cada vez que alguien lo escriba se ponga en hyperlink y le
 * pasamos por encima y diga contractor". La lista vive en el servidor (Settings → Acronyms) y acá
 * se cachea en memoria y en localStorage para que el primer mensaje ya salga marcado.
 *
 * Marca palabra ENTERA y mayúsculas exactas (`\bCO\b`): "CO2", "Colorado" y "co-owner" no cuentan.
 * En HTML solo toca los TEXTOS (nunca etiquetas ni atributos) y salta lo que está dentro de un
 * <a> o de una @mención, para no anidar un <abbr> adentro de un link. La salida es la propia
 * `<abbr class="acr" title="…">`, con el título escapado: no hay forma de meter HTML por acá.
 */
export type Acronimo = { id?: string; acronym: string; meaning: string };

const ACRONIMO_RE = /^[A-Z0-9]{2,8}$/;
const CLAVE_LS = 'acronimos:v1';
let lista: Acronimo[] = [];
let re: RegExp | null = null;
let porClave = new Map<string, string>();
const oyentes = new Set<() => void>();

try { const raw = localStorage.getItem(CLAVE_LS); if (raw) fijarAcronimos(JSON.parse(raw) as Acronimo[]); } catch { /* sin storage */ }

const escapar = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const escapeAttr = (v: string) => v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

export function fijarAcronimos(nueva: Acronimo[]): void {
  lista = (nueva ?? []).filter((a) => a && ACRONIMO_RE.test(a.acronym) && (a.meaning ?? '').trim());
  porClave = new Map(lista.map((a) => [a.acronym, a.meaning.trim()]));
  const claves = [...porClave.keys()].sort((a, b) => b.length - a.length);
  re = claves.length ? new RegExp(`\\b(${claves.map(escapar).join('|')})\\b`, 'g') : null;
  try { localStorage.setItem(CLAVE_LS, JSON.stringify(lista)); } catch { /* incógnito */ }
  for (const fn of oyentes) fn();
}

export const acronimosActuales = (): Acronimo[] => lista;

/** Trae la lista del servidor (una vez al entrar; Settings la vuelve a pedir al editar). */
export async function cargarAcronimos(): Promise<void> {
  try { fijarAcronimos((await api.get<{ acronyms: Acronimo[] }>('/api/acronyms')).acronyms); } catch { /* la copia local sigue valiendo */ }
}

export function suscribirAcronimos(fn: () => void): () => void { oyentes.add(fn); return () => { oyentes.delete(fn); }; }

/** Texto plano → nodos: cada acrónimo va en <abbr> con su significado. */
export function marcarAcronimosNodes(texto: string, keyBase = 'a'): ReactNode[] {
  if (!re || !texto) return [texto];
  const out: ReactNode[] = [];
  let last = 0; let i = 0;
  re.lastIndex = 0;
  for (let m = re.exec(texto); m; m = re.exec(texto)) {
    if (m.index > last) out.push(texto.slice(last, m.index));
    out.push(<abbr key={`${keyBase}-${i++}`} className="acr" title={porClave.get(m[0]) ?? ''}>{m[0]}</abbr>);
    last = m.index + m[0].length;
  }
  if (last < texto.length) out.push(texto.slice(last));
  return out;
}

const marcarTexto = (t: string) => re ? t.replace(re, (m) => `<abbr class="acr" title="${escapeAttr(porClave.get(m) ?? '')}">${m}</abbr>`) : t;

/**
 * HTML ya saneado → el mismo HTML con los acrónimos de los textos en <abbr>. Salta el interior de
 * <a> y de las @menciones (<span class="msg-mention">). Solo mira texto entre etiquetas: los
 * atributos quedan intactos.
 */
export function marcarAcronimosHtml(html: string): string {
  if (!re || !html) return html;
  const tagRe = /<\/?([a-zA-Z][a-zA-Z0-9]*)\b[^>]*>/g;
  let out = ''; let last = 0;
  // Pila de etiquetas abiertas; "callado" = hay un <a> o una mención abierta por encima.
  const pila: Array<{ tag: string; callado: boolean }> = [];
  const callado = () => pila.some((p) => p.callado);
  for (let m = tagRe.exec(html); m; m = tagRe.exec(html)) {
    const texto = html.slice(last, m.index);
    out += callado() ? texto : marcarTexto(texto);
    out += m[0];
    last = m.index + m[0].length;
    const tag = m[1]!.toLowerCase();
    const cierra = m[0].startsWith('</');
    const vacia = tag === 'br' || tag === 'img' || m[0].endsWith('/>');
    if (cierra) {
      const idx = pila.map((p) => p.tag).lastIndexOf(tag);
      if (idx >= 0) pila.splice(idx);
    } else if (!vacia) {
      pila.push({ tag, callado: tag === 'a' || /\bclass="[^"]*\bmsg-mention\b/.test(m[0]) });
    }
  }
  const resto = html.slice(last);
  out += callado() ? resto : marcarTexto(resto);
  return out;
}

/**
 * QUITA la marca (<abbr class="acr" title="…">CO</abbr> → CO). Es un adorno de PANTALLA: si el HTML
 * va a volver al editor o al servidor, no puede llevarla. Del 09-24 al 09-29 quince notas editadas
 * se guardaron con la marca (el saneador la ponía y el mismo saneador sembraba el editor) y salían
 * en pantalla como `&lt;abbr class="acr"…`. El backend también la quita al guardar (lib/acronimos).
 */
export function quitarMarcasDeAcronimos(html: string): string {
  let out = html ?? '';
  if (!out.includes('<abbr')) return out;
  const re = /<abbr\b[^>]*\bclass="acr"[^>]*>([\s\S]*?)<\/abbr>/gi;
  for (let i = 0; i < 5 && re.test(out); i += 1) { re.lastIndex = 0; out = out.replace(re, '$1'); re.lastIndex = 0; }
  return out;
}
